import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EstadoError, EstadoVacio, ListaEsqueleto } from '../../components/ui/Estados';
import { Icon } from '../../components/ui/Icon';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { Tabla, type Columna } from '../../components/ui/Tabla';
import { BarraFiltros } from '../../components/layout/BarraFiltros';
import { CabeceraPagina } from '../../components/layout/CabeceraPagina';
import {
  repositorioNotificaciones,
  type ContactoAlerta,
  type DatosContacto,
} from '../../../data/repositories/RepositorioNotificaciones';
import { repositorioCatalogos } from '../../../data/repositories/RepositorioCatalogos';
import { ErrorApp, registrarError } from '../../../domain/errores';
import { useSesionStore } from '../../stores/sesionStore';
import estilos from '../../components/layout/PaginaCrud.module.css';
import { FichaRegistro } from '../../components/layout/FichaRegistro';

const CAMPOS_VACIOS = {
  nombre: '',
  clienteId: '',
  email: '',
  whatsapp: '',
  telegram: '',
  cargo: '',
  notas: '',
  activo: true,
};

export function Contactos() {
  const clienteConsultas = useQueryClient();
  const politica = useSesionStore((s) => s.politica);
  const puedeGestionar = politica?.puede('gestionar_notificaciones') ?? false;
  const sinPermiso = puedeGestionar ? null : 'Tu rol no puede modificar los contactos';

  const [busqueda, setBusqueda] = useState('');

  // Registro cuya ficha de consulta esta abierta.
  const [enFicha, setEnFicha] = useState<ContactoAlerta | null>(null);
  const [clienteFiltro, setClienteFiltro] = useState<number | null>(null);
  const [estadoFiltro, setEstadoFiltro] = useState<'' | 'activos' | 'inactivos'>('');
  const [abierto, setAbierto] = useState(false);
  const [enEdicion, setEnEdicion] = useState<ContactoAlerta | null>(null);
  const [campos, setCampos] = useState(CAMPOS_VACIOS);
  const [error, setError] = useState<string | null>(null);

  const contactos = useQuery({
    queryKey: ['contactos', clienteFiltro],
    queryFn: () => repositorioNotificaciones.listarContactos(clienteFiltro),
  });

  const clientes = useQuery({
    queryKey: ['catalogo', 'clientes'],
    queryFn: () => repositorioCatalogos.clientes(),
    staleTime: 5 * 60_000,
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ['contactos'] });
  };

  const guardar = useMutation({
    mutationFn: (datos: DatosContacto) =>
      enEdicion
        ? repositorioNotificaciones.actualizarContacto(enEdicion.id, datos)
        : repositorioNotificaciones.crearContacto(datos),
    onSuccess: () => {
      toast.success(enEdicion ? 'Contacto actualizado' : 'Contacto creado');
      setAbierto(false);
      setEnEdicion(null);
      refrescar();
    },
    onError: (e) => {
      registrarError('contactos.guardar', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo guardar el contacto');
    },
  });

  const eliminar = useMutation({
    mutationFn: (id: number) => repositorioNotificaciones.eliminarContacto(id),
    onSuccess: () => {
      toast.success('Contacto eliminado');
      refrescar();
    },
    onError: (e) => {
      registrarError('contactos.eliminar', e);
      toast.error(e instanceof ErrorApp ? e.message : 'No se pudo eliminar el contacto');
    },
  });

  const alternar = useMutation({
    mutationFn: ({ id, activo }: { id: number; activo: boolean }) =>
      repositorioNotificaciones.cambiarActivoContacto(id, activo),
    onSuccess: refrescar,
    onError: () => toast.error('No se pudo cambiar el estado del contacto'),
  });

  const lista = useMemo(() => contactos.data ?? [], [contactos.data]);

  const filtrados = useMemo(() => {
    const q = busqueda.trim().toLowerCase();

    return lista.filter((c) => {
      if (estadoFiltro === 'activos' && !c.activo) return false;
      if (estadoFiltro === 'inactivos' && c.activo) return false;
      if (!q) return true;

      return `${c.nombre} ${c.email ?? ''} ${c.whatsapp ?? ''} ${c.cargo ?? ''} ${c.clienteNombre ?? ''}`
        .toLowerCase()
        .includes(q);
    });
  }, [lista, busqueda, estadoFiltro]);

  const abrirNuevo = () => {
    setEnEdicion(null);
    setCampos(CAMPOS_VACIOS);
    setError(null);
    setAbierto(true);
  };

  const abrirEdicion = (c: ContactoAlerta) => {
    setEnEdicion(c);
    setCampos({
      nombre: c.nombre,
      clienteId: String(c.clienteId ?? ''),
      email: c.email ?? '',
      whatsapp: c.whatsapp ?? '',
      telegram: c.telegram ?? '',
      cargo: c.cargo ?? '',
      notas: c.notas ?? '',
      activo: c.activo,
    });
    setError(null);
    setAbierto(true);
  };

  const enviar = () => {
    setError(null);

    if (!campos.nombre.trim()) {
      setError('Indica el nombre del contacto.');
      return;
    }
    if (!campos.clienteId) {
      setError('Elige a qué cliente pertenece este contacto.');
      return;
    }

    // Un contacto sin ninguna vía de aviso nunca recibiría nada: es un error
    // silencioso que sólo se descubre cuando falla una alarma de verdad.
    if (!campos.email.trim() && !campos.whatsapp.trim() && !campos.telegram.trim()) {
      setError('Indica al menos una forma de avisar: correo, WhatsApp o Telegram.');
      return;
    }

    const correo = campos.email.trim();
    if (correo && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) {
      setError('El correo no tiene un formato válido.');
      return;
    }

    guardar.mutate({
      nombre: campos.nombre.trim(),
      cliente_id: Number(campos.clienteId),
      email: correo || null,
      telefono_whatsapp: campos.whatsapp.trim() || null,
      telegram_chat_id: campos.telegram.trim() || null,
      rol_cargo: campos.cargo.trim() || null,
      notas: campos.notas.trim() || null,
      activo: campos.activo,
    });
  };

  const columnas: Columna<ContactoAlerta>[] = [
    {
      clave: 'nombre',
      encabezado: 'Contacto',
      movil: 'titulo',
      render: (c) => (
        <span>
          <span className={estilos.nombre}>{c.nombre}</span>
          {c.cargo && <span className={estilos.secundario}> · {c.cargo}</span>}
        </span>
      ),
    },
    {
      clave: 'cliente',
      encabezado: 'Cliente',
      movil: 'subtitulo',
      render: (c) => c.clienteNombre ?? '—',
    },
    {
      clave: 'vias',
      encabezado: 'Se le avisa por',
      render: (c) => {
        const vias: string[] = [];
        if (c.email) vias.push('Correo');
        if (c.whatsapp) vias.push('WhatsApp');
        if (c.telegram) vias.push('Telegram');

        if (vias.length === 0) {
          return (
            <Badge color="var(--status-danger)" title="Este contacto no recibirá ningún aviso">
              Sin vía de aviso
            </Badge>
          );
        }

        return (
          <span style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
            {vias.map((v) => (
              <Badge key={v} color="var(--status-info)">
                {v}
              </Badge>
            ))}
          </span>
        );
      },
    },
    {
      clave: 'contacto',
      encabezado: 'Datos',
      render: (c) => (
        <span className={estilos.secundario}>{c.email ?? c.whatsapp ?? c.telegram ?? '—'}</span>
      ),
    },
    {
      clave: 'estado',
      encabezado: 'Estado',
      render: (c) =>
        c.activo ? (
          <Badge color="var(--status-success)">Activo</Badge>
        ) : (
          <Badge color="var(--text-3)">Inactivo</Badge>
        ),
    },
    {
      clave: 'acciones',
      encabezado: 'Acciones',
      alinear: 'centro',
      movil: 'accion',
      render: (c) => (
        <div className={estilos.acciones}>
          <Button
            tamano="sm"
            variante="secundario"
            icono={c.activo ? 'interruptorOn' : 'interruptorOff'}
            title={c.activo ? 'Desactivar' : 'Activar'}
            motivoDeshabilitado={sinPermiso}
            onClick={() => alternar.mutate({ id: c.id, activo: !c.activo })}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="ver"
            title="Ver datos"
            onClick={() => setEnFicha(c)}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="editar"
            title="Editar"
            motivoDeshabilitado={sinPermiso}
            onClick={() => abrirEdicion(c)}
          />
          <Button
            tamano="sm"
            variante="secundario"
            icono="eliminar"
            title="Eliminar"
            motivoDeshabilitado={sinPermiso}
            onClick={() => {
              const seguro = window.confirm(
                `¿Eliminar a «${c.nombre}» de los contactos de alerta?\n\n` +
                  'Dejará de recibir avisos y se quitará de las cadenas de escalamiento.',
              );
              if (seguro) eliminar.mutate(c.id);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <div className={estilos.pagina}>
      <CabeceraPagina
        titulo="Contactos de alerta"
        descripcion="Personas a las que se avisa cuando salta una alarma."
        acciones={
          <>
            <Button
              variante="secundario"
              icono="refrescar"
              compactoEnMovil
              onClick={refrescar}
              cargando={contactos.isFetching}
            >
              Actualizar
            </Button>
            <Button icono="agregar" motivoDeshabilitado={sinPermiso} onClick={abrirNuevo}>
              Nuevo contacto
            </Button>
          </>
        }
      />

      <BarraFiltros>
        <Input
          etiqueta="Buscar"
          icono="buscar"
          type="search"
          name="filtro-listado"
          sinAutocompletar
          placeholder="Nombre, correo, cargo…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
        <Select
          etiqueta="Cliente"
          placeholder="Todos los clientes"
          value={clienteFiltro ?? ''}
          onChange={(e) => setClienteFiltro(e.target.value ? Number(e.target.value) : null)}
          opciones={(clientes.data ?? []).map((c) => ({ valor: c.id, etiqueta: c.nombre }))}
        />
        <Select
          etiqueta="Estado"
          placeholder="Todos"
          value={estadoFiltro}
          onChange={(e) => setEstadoFiltro(e.target.value as '' | 'activos' | 'inactivos')}
          opciones={[
            { valor: 'activos', etiqueta: 'Activos' },
            { valor: 'inactivos', etiqueta: 'Inactivos' },
          ]}
        />
      </BarraFiltros>

      {contactos.isLoading ? (
        <ListaEsqueleto filas={5} />
      ) : contactos.isError ? (
        <EstadoError
          acciones={
            <Button variante="secundario" icono="refrescar" onClick={refrescar}>
              Reintentar
            </Button>
          }
        />
      ) : filtrados.length === 0 ? (
        <EstadoVacio
          icono="contactos"
          titulo={lista.length === 0 ? 'Sin contactos de alerta' : 'Ninguno coincide'}
          descripcion={
            lista.length === 0
              ? 'Sin contactos, las alarmas quedan registradas pero nadie recibe aviso.'
              : 'Prueba con otra búsqueda.'
          }
          acciones={
            lista.length === 0 ? (
              <Button icono="agregar" motivoDeshabilitado={sinPermiso} onClick={abrirNuevo}>
                Crear el primero
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <Tabla
            datos={filtrados}
            columnas={columnas}
            claveFila={(c) => c.id}
            etiqueta="Contactos de alerta"
          />

          <FichaRegistro
            item={enFicha}
            titulo="Contacto de alerta"
            columnas={columnas}
            onCerrar={() => setEnFicha(null)}
          />
        </>
      )}

      <Modal
        abierto={abierto}
        onCerrar={() => setAbierto(false)}
        titulo={enEdicion ? 'Editar contacto' : 'Nuevo contacto'}
        descripcion="Recibirá aviso por cada vía de la que tenga dato."
        pie={
          <>
            <Button variante="secundario" onClick={() => setAbierto(false)}>
              Cancelar
            </Button>
            <Button onClick={enviar} cargando={guardar.isPending} icono="guardar">
              Guardar
            </Button>
          </>
        }
      >
        <div className={estilos.formulario}>
          {error && (
            <div className={estilos.error}>
              <Icon nombre="errorContorno" tamano={15} />
              {error}
            </div>
          )}

          <Input
            etiqueta="Nombre"
            requerido
            placeholder="Nombre y apellido"
            value={campos.nombre}
            onChange={(e) => setCampos((c) => ({ ...c, nombre: e.target.value }))}
          />

          <Select
            etiqueta="Cliente"
            requerido
            placeholder="Elige un cliente"
            value={campos.clienteId}
            onChange={(e) => setCampos((c) => ({ ...c, clienteId: e.target.value }))}
            opciones={(clientes.data ?? []).map((c) => ({ valor: c.id, etiqueta: c.nombre }))}
          />

          <Input
            etiqueta="Cargo"
            placeholder="Opcional: jefe de planta, vigilante…"
            value={campos.cargo}
            onChange={(e) => setCampos((c) => ({ ...c, cargo: e.target.value }))}
          />

          <Input
            etiqueta="Correo"
            type="email"
            icono="correo"
            placeholder="nombre@empresa.com"
            value={campos.email}
            onChange={(e) => setCampos((c) => ({ ...c, email: e.target.value }))}
            ayuda="Si lo dejas vacío, no recibirá avisos por correo"
          />

          <Input
            etiqueta="WhatsApp"
            icono="telefono"
            placeholder="+58 412 1234567"
            value={campos.whatsapp}
            onChange={(e) => setCampos((c) => ({ ...c, whatsapp: e.target.value }))}
            ayuda="Con el código de país, tal como se marca desde el extranjero"
          />

          <Input
            etiqueta="Telegram"
            placeholder="Identificador de conversación"
            value={campos.telegram}
            onChange={(e) => setCampos((c) => ({ ...c, telegram: e.target.value }))}
            ayuda="Se obtiene al iniciar una conversación con el bot de avisos"
          />

          <Input
            etiqueta="Notas"
            placeholder="Opcional: horario de disponibilidad, indicaciones…"
            value={campos.notas}
            onChange={(e) => setCampos((c) => ({ ...c, notas: e.target.value }))}
          />

          <label className={estilos.opcion}>
            <input
              type="checkbox"
              checked={campos.activo}
              onChange={(e) => setCampos((c) => ({ ...c, activo: e.target.checked }))}
            />
            <span>
              <strong>Contacto activo</strong>
              <span className={estilos.ayuda}>Si se desactiva, deja de recibir avisos</span>
            </span>
          </label>
        </div>
      </Modal>
    </div>
  );
}
